/*
 * SessionRunner — a mixed practice session as one conversation (#/practice/session).
 *
 * Only sessions with two or more practice types land here; a single type opens
 * that type's own focused screen (NanoApp.createRun). The tutor introduces the
 * session, then every activity — MCQ, flashcard, theory question, case study,
 * rapid-fire question — arrives as a message. Your answer stays in the thread
 * as your message, the evaluation follows as the tutor's, and the next
 * activity appears below. The action dock at the bottom adapts to the current
 * activity (options + Submit, Reveal / Got it, a text area…).
 *
 * Content and grading reuse the existing engine endpoints, and answers are
 * logged to learning memory exactly as the standalone modes log them. The
 * tutor's own lines are fixed copy — only grading and case coaching come from
 * the AI. Each finished activity is stored with its question, your answer and
 * the feedback, in sessionStorage, so Pause → Resume restores the whole thread.
 *
 * The steps arrive already interleaved (sessionPlan.buildSteps), so the
 * session has ONE progression — "4 of 15 activities" — never per-type blocks.
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import '../../styles/prepare-up-home.css';
import '../../styles/prepare-up-hub.css';
import '../../styles/prepare-up-session.css';
import APIService from '../../services/APIService';
import IvShell from './InterviewChrome';
import * as sessionCache from './sessionCache';
import { indexTree } from './paths';
import { logFlashcard, logMcqAnswered, logRapidfireQuestion } from './memoryLog';
import { chatJson, finalPrompt, startPrompt, turnPrompt, withRetry } from './CaseStudyMode';
import { actOf, saveRun, clearRun, saveSessionRecord } from './sessionPlan';
import {
  IconArrowDown, IconArrowRight, IconBolt, IconBook, IconCards, IconCheck, IconClock, IconList, IconPause, IconTarget, IconX,
} from './icons';

const LETTERS = ['A', 'B', 'C', 'D'];
const pct = (v) => `${Math.round((v || 0) * 100)}%`;
const KIND_ICON = { mcq: IconList, flashcards: IconCards, theory: IconBook, case: IconTarget, rapid: IconBolt };
/* The tutor's mark: a small, friendly face (no brand, no mascot). */
const Tutor = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 3v3" /><circle cx="12" cy="2.6" r="1" fill="currentColor" stroke="none" />
    <rect x="4" y="6" width="16" height="13" rx="5" />
    <circle cx="9.2" cy="12" r="1.3" fill="currentColor" stroke="none" /><circle cx="14.8" cy="12" r="1.3" fill="currentColor" stroke="none" />
    <path d="M9.5 15.6c1.5 1 3.5 1 5 0" />
  </svg>
);
const timeOf = (ms) => (ms ? new Date(ms).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '');
const listJoin = (a) => (a.length < 2 ? a.join('') : `${a.slice(0, -1).join(', ')} and ${a[a.length - 1]}`);

/* ── fetching one item per step (batched where the endpoint is batched) ── */
const useStepItems = ({ steps, scope, topicNames }) => {
  const items = useRef({});
  const batches = useRef({});
  const recent = useRef([]);
  const inflight = useRef({});

  const fetchItem = useCallback(async (i) => {
    const s = steps[i];
    if (!s) return null;
    if (s.type === 'mcq') {
      const q = sessionCache.take(scope, 'mcq') || await APIService.engineMcq({ conceptIds: scope, recent: recent.current.slice(-12) });
      if (!q?.question) throw new Error('No MCQ came back for this topic.');
      return q;
    }
    if (s.type === 'theory') {
      const q = sessionCache.take(scope, 'theory') || await APIService.engineTheoryNext({ conceptIds: scope, recent: recent.current.slice(-12) });
      if (!q?.question) throw new Error('No theory question came back.');
      return q;
    }
    if (s.type === 'flashcards') {
      if (!batches.current.flashcards) {
        const cached = sessionCache.peekAll(scope, 'flashcards') || [];
        batches.current.flashcards = cached.length >= s.of ? cached : (await APIService.engineFlashcards({ conceptIds: scope, count: s.of }))?.cards || [];
      }
      const c = batches.current.flashcards[s.n - 1];
      if (!c) throw new Error('There aren’t enough flashcards for this topic yet.');
      return c;
    }
    if (s.type === 'rapid') {
      if (!batches.current.rapid) {
        const r = await APIService.engineRapidfire({ conceptIds: scope, count: s.of + 2 });
        batches.current.rapid = (r?.questions || []).filter((q) => q.kind !== 'behavioral').map((q) => ({ ...q, answer: q.answer || q.reference_answer || '' }));
      }
      const q = batches.current.rapid[s.n - 1];
      if (!q) throw new Error('There aren’t enough rapid-fire questions for this topic yet.');
      return q;
    }
    if (s.type === 'case') {
      const k = (s.n - 1) % Math.max(1, topicNames.length);
      const cs = await withRetry(() => chatJson(startPrompt([...topicNames.slice(k), ...topicNames.slice(0, k)])));
      const stages = Array.isArray(cs.stages) && cs.stages.length ? cs.stages.slice(0, 5) : ['Understand the problem', 'Propose a solution'];
      return { ...cs, stages };
    }
    return null;
  }, [steps, scope, topicNames]);

  const get = useCallback((i) => {
    if (items.current[i]) return Promise.resolve(items.current[i]);
    if (!inflight.current[i]) {
      inflight.current[i] = fetchItem(i).then((it) => {
        items.current[i] = it;
        if (it?.question) recent.current.push(it.question);
        return it;
      }).finally(() => { delete inflight.current[i]; });
    }
    return inflight.current[i];
  }, [fetchItem]);
  return { get, peek: (i) => items.current[i] };
};

/* ── message primitives ─────────────────────────────────────────────────── */
const Bot = ({ children, className = '', at }) => (
  <div className={`sc-msg bot ${className}`}>
    <span className="sc-avatar" aria-hidden="true"><Tutor size={18} /></span>
    <div className="sc-col">
      <div className="sc-bubble">{children}</div>
      {at ? <time className="sc-time">{timeOf(at)}</time> : null}
    </div>
  </div>
);
const Me = ({ children, initial, at }) => (
  <div className="sc-msg me">
    <div className="sc-col">
      <div className="sc-bubble"><span className="pu-sr">You: </span>{children}</div>
      {at ? <time className="sc-time">{timeOf(at)}</time> : null}
    </div>
    <span className="sc-avatar me" aria-hidden="true">{initial}</span>
  </div>
);
const Head = ({ type, topic, n, total }) => {
  const Icon = KIND_ICON[type];
  return (
    <div className="sc-head">
      <span className={`sc-kind k-${type}`}><Icon size={13} /> {actOf(type).one}</span>
      {topic && <span className="sc-topic">{topic}</span>}
      <span className="sc-count">Activity {n} of {total}</span>
    </div>
  );
};
const Verdict = ({ tone, title, children }) => (
  <div className={`sc-verdict ${tone}`}>
    <b>{tone === 'ok' && <IconCheck size={14} />} {title}</b>
    {children}
  </div>
);
const Lists = ({ a, aTitle, b, bTitle }) => ((a || []).length || (b || []).length ? (
  <div className="sc-fb">
    {(a || []).length > 0 && <div><h4>{aTitle}</h4><ul>{a.map((x, i) => <li key={i}>{x}</li>)}</ul></div>}
    {(b || []).length > 0 && <div><h4>{bTitle}</h4><ul>{b.map((x, i) => <li key={i}>{x}</li>)}</ul></div>}
  </div>
) : null);

/* ── one activity, as messages ─────────────────────────────────────────────
 * Renders an activity's whole exchange from its record: the question, your
 * answer, the evaluation. `live` adds the interactive bits (option picking,
 * card reveal) for the current activity; finished ones are read-only. */
const Exchange = ({ step, idx, total, item, rec, live, draft, setDraft, topic, initial }) => {
  const head = <Head type={step.type} topic={topic} n={idx + 1} total={total} />;
  if (step.type === 'mcq') {
    const answered = !!rec?.pick;
    const pick = answered ? rec.pick : draft.pick;
    const opts = LETTERS.filter((L) => item.options?.[L]);
    return (
      <>
        <Bot>
          {head}
          <p className="sc-q">{item.question}</p>
          <div className="sc-opts" role="radiogroup" aria-label={`Answer options for activity ${idx + 1}`}>
            {opts.map((L) => {
              const state = !answered ? (pick === L ? 'picked' : '') : L === item.answer ? 'right' : pick === L ? 'wrong' : 'dim';
              return (
                <button key={L} type="button" role="radio" aria-checked={pick === L} disabled={!live || answered}
                  className={`sc-opt ${state}`} onClick={() => setDraft({ ...draft, pick: L })}>
                  <span className="sc-opt-l">{L}</span><span>{item.options[L]}</span>
                  {answered && L === item.answer && <><IconCheck size={14} /><span className="pu-sr"> (correct answer)</span></>}
                </button>
              );
            })}
          </div>
        </Bot>
        {answered && <Me initial={initial} at={rec.at}><b>{rec.pick}.</b> {item.options[rec.pick]}</Me>}
        {answered && (
          <Bot>
            <Verdict tone={rec.correct ? 'ok' : 'bad'} title={rec.correct ? 'Correct!' : `Not quite — the answer is ${item.answer}.`}>
              {item.explanation && <p>{item.explanation}</p>}
            </Verdict>
          </Bot>
        )}
      </>
    );
  }
  if (step.type === 'flashcards') {
    const shown = !!rec || draft.flipped;
    return (
      <>
        <Bot>
          {head}
          <p className="sc-lead">Let’s reinforce a key concept. Think of the answer first, then reveal it.</p>
          <button type="button" className={`sc-card${shown ? ' shown' : ''}`} disabled={!live || !!rec}
            onClick={() => setDraft({ ...draft, flipped: !draft.flipped })} aria-expanded={shown}
            aria-label={shown ? undefined : `Flashcard: ${item.question}. Reveal the answer`}>
            <span className="sc-card-front"><small>Question</small><b>{item.question}</b>{!shown && <em>Click to reveal the answer</em>}</span>
            {shown && <span className="sc-card-back"><small>Answer</small><span>{item.answer}</span></span>}
          </button>
        </Bot>
        {rec && <Me initial={initial} at={rec.at}>{rec.rating === 'good' ? 'Got it' : 'Review again'}</Me>}
      </>
    );
  }
  if (step.type === 'theory' || step.type === 'rapid') {
    const g = rec?.grade;
    return (
      <>
        <Bot>{head}<p className="sc-q">{item.question}</p>{step.type === 'rapid' && !rec && <p className="sc-lead">A short answer is enough.</p>}</Bot>
        {rec && <Me initial={initial} at={rec.at}>{rec.skipped ? <em>Skipped</em> : rec.answer}</Me>}
        {rec && !rec.skipped && step.type === 'theory' && g && (
          <Bot>
            <Verdict tone={g.score >= 70 ? 'ok' : g.score >= 40 ? 'mid' : 'bad'} title={`${g.score}/100 · AI-evaluated`}>
              <Lists a={(g.strengths || []).slice(0, 3)} aTitle="What you covered" b={(g.gaps || []).slice(0, 3)} bTitle="Missing considerations" />
              {(g.key_points || []).length > 0 && <p><b>To improve:</b> a strong answer covers {g.key_points.slice(0, 3).join('; ')}.</p>}
            </Verdict>
          </Bot>
        )}
        {rec && !rec.skipped && step.type === 'rapid' && g && (
          <Bot>
            <Verdict tone={g.correct ? 'ok' : 'bad'} title={g.correct ? 'Correct!' : 'Needs review'}>
              {g.feedback && <p>{g.feedback}</p>}
              {!g.correct && item.answer && <p><b>A strong answer:</b> {item.answer}</p>}
            </Verdict>
          </Bot>
        )}
      </>
    );
  }
  // case study: scenario → your approach → coaching + follow-up → … → evaluation
  const turns = rec?.turns || draft.turns || [];
  const final = rec?.final;
  return (
    <>
      <Bot>
        {head}
        <p className="sc-case-title">{item.title}</p>
        <p className="sc-scenario">{item.scenario}</p>
        <p className="sc-q">{item.question}</p>
      </Bot>
      {turns.map((t, k) => (
        <React.Fragment key={k}>
          <Me initial={initial} at={t.at}>{t.a}</Me>
          {t.reply && (
            <Bot>
              <Verdict tone={t.reply.verdict === 'strong' ? 'ok' : t.reply.verdict === 'weak' ? 'bad' : 'mid'} title={t.reply.headline || 'Feedback'}>
                {t.reply.feedback && <p>{t.reply.feedback}</p>}
                {(t.reply.improve || []).length > 0 && <p><b>Think about:</b> {t.reply.improve.join('; ')}</p>}
              </Verdict>
              {t.next && <p className="sc-q sc-follow">{t.next}</p>}
            </Bot>
          )}
        </React.Fragment>
      ))}
      {final && (
        <Bot>
          <Verdict tone={final.score >= 7 ? 'ok' : final.score >= 4 ? 'mid' : 'bad'} title={`${final.headline || 'Case review'} · ${final.score}/10 · AI-evaluated`}>
            {final.summary && <p>{final.summary}</p>}
            <Lists a={final.strengths} aTitle="Strengths" b={final.improve} bTitle="To improve" />
            {final.model_answer && <details className="sc-model"><summary>A strong approach</summary><p>{final.model_answer}</p></details>}
          </Verdict>
        </Bot>
      )}
    </>
  );
};

/* ── the session ────────────────────────────────────────────────────────── */
export default function SessionRunner({ run: initialRun, hub, onExit, onNewSession, onOpenHubNode, ...shell }) {
  const [run, setRun] = useState(initialRun);              // { cfg, idx, results[], startedAt }
  const { cfg } = run;
  const steps = cfg.steps;
  const total = steps.length;
  const byId = useMemo(() => indexTree(hub || []), [hub]);
  const nameOf = useCallback((cid, depth = 2) => {
    const parts = String(cid || '').split('.');
    for (let i = Math.min(depth, parts.length); i > 0; i -= 1) { const n = byId[parts.slice(0, i).join('.')]; if (n) return n.name; }
    return parts[parts.length - 1]?.replace(/_/g, ' ') || '';
  }, [byId]);
  const topicNames = useMemo(() => cfg.scopeIds.map((id) => byId[id]?.name || id).slice(0, 6), [cfg.scopeIds, byId]);
  const { get, peek } = useStepItems({ steps, scope: cfg.scopeIds, topicNames });
  const [item, setItem] = useState(null);
  const [err, setErr] = useState('');
  const [attempt, setAttempt] = useState(0);              // bumps to re-fetch after an error
  const [draft, setDraft] = useState({});                 // the live activity's in-progress state
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [actErr, setActErr] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [flowOpen, setFlowOpen] = useState(false);
  const done = run.idx >= total;
  const step = steps[run.idx];
  const rec = run.results[run.idx];                        // set once the live activity is answered
  const displayName = shell.profile?.display_name || shell.profile?.name || '';
  const initial = (displayName || 'You').slice(0, 1).toUpperCase();
  const topicOf = (it) => (it?.concept_ids?.[0] ? nameOf(it.concept_ids[0]) : '');

  useEffect(() => { saveRun(run); }, [run]);
  useEffect(() => {
    if (done) return undefined;
    let live = true;
    const stored = run.results[run.idx]?.item;
    setErr('');
    if (stored) { setItem(stored); return undefined; }
    setItem(null);
    get(run.idx).then((it) => { if (live) setItem(it); }).catch((e) => { if (live) setErr(e.message || 'Could not load this activity.'); });
    get(run.idx + 1).catch(() => {});                     // prefetch the next one
    return () => { live = false; };
  }, [run.idx, done, get, attempt]); // eslint-disable-line react-hooks/exhaustive-deps

  /* recording + moving on */
  const commit = (r) => setRun((x) => {
    const results = [...x.results];
    results[x.idx] = { type: steps[x.idx].type, item, at: Date.now(), concept_ids: item?.concept_ids || cfg.scopeIds, ...r };
    return { ...x, results };
  });
  const advance = () => { setDraft({}); setText(''); setActErr(''); setRun((x) => ({ ...x, idx: x.idx + 1 })); };
  const commitAndAdvance = (r) => {
    setDraft({}); setText(''); setActErr('');
    setRun((x) => {
      const results = [...x.results];
      results[x.idx] = { type: steps[x.idx].type, item, at: Date.now(), concept_ids: item?.concept_ids || cfg.scopeIds, ...r };
      return { ...x, results, idx: x.idx + 1 };
    });
  };

  const submitMcq = () => {
    if (!draft.pick) return;
    const correct = draft.pick === item.answer;
    logMcqAnswered(item.concept_ids || [], correct, { question: item.question, selected: draft.pick, answer: item.answer, item_id: item.item_id || null, depth: item.depth || null });
    commit({ pick: draft.pick, correct });
  };
  const rateFlash = (key) => {
    logFlashcard(item.concept_ids || [], key, { item_id: item.item_id || null, depth: item.depth || null });
    commitAndAdvance({ rating: key, correct: key === 'good' });
  };
  const submitTheory = async () => {
    if (!text.trim() || busy) return;
    setBusy(true); setActErr('');
    try {
      const grade = await APIService.engineTheoryGrade({ question: item.question, answer: text, conceptIds: item.concept_ids || cfg.scopeIds, itemId: item.item_id || null, depth: item.depth || null });
      commit({ answer: text, grade, score: grade?.score ?? 0, gaps: grade?.gaps || [] });
    } catch (e) { setActErr(e.message || 'Grading failed — try again.'); } finally { setBusy(false); }
  };
  const submitRapid = async () => {
    if (!text.trim() || busy) return;
    setBusy(true); setActErr('');
    try {
      const r = await APIService.engineBlitzGrade({ items: [{ question: item.question, reference_answer: item.answer || '', answer: text, kind: 'technical', concept_ids: item.concept_ids || [] }] });
      const g = (r?.results || [])[0];
      if (!g) throw new Error('No grade came back.');
      logRapidfireQuestion(item.concept_ids || [], g.correct ? 'correct' : 'wrong');
      commit({ answer: text, grade: g, correct: !!g.correct });
    } catch (e) { setActErr(e.message || 'Grading failed — try again.'); } finally { setBusy(false); }
  };
  const finishCase = async (turns) => {
    setBusy(true); setActErr('');
    try {
      const history = turns.map((t) => ({ q: t.q, a: t.a }));
      const final = await withRetry(() => chatJson(finalPrompt({ cs: item, history })));
      commit({ turns, final, score: (final.score || 0) * 10, title: item.title, concept_ids: cfg.scopeIds });
    } catch (e) { setActErr(e.message || 'Could not review the case — try again.'); } finally { setBusy(false); }
  };
  const submitCase = async () => {
    if (!text.trim() || busy) return;
    const turns = draft.turns || [];
    const question = turns.length ? turns[turns.length - 1].next : item.question;
    setBusy(true); setActErr('');
    try {
      const history = turns.map((t) => ({ q: t.q, a: t.a }));
      const reply = await withRetry(() => chatJson(turnPrompt({ cs: item, stage: Math.min(turns.length, item.stages.length - 1), attempt: 1, history, question, message: text })));
      const followUp = turns.length < 1 && reply.follow_up ? reply.follow_up : null;   // one follow-up, then the evaluation
      const nextTurns = [...turns, { q: question, a: text, at: Date.now(), reply, next: followUp }];
      setDraft({ ...draft, turns: nextTurns }); setText('');
      setBusy(false);
      if (!followUp) await finishCase(nextTurns);
    } catch (e) { setActErr(e.message || 'The AI coach is unavailable — try again.'); setBusy(false); }
  };
  const skipActivity = () => commitAndAdvance({ skipped: true, item: item || null });

  const finishEarly = () => setRun((r) => {
    const results = [...r.results];
    for (let i = r.idx; i < total; i += 1) if (!results[i]) results[i] = { type: steps[i].type, skipped: true, notReached: true };
    return { ...r, results, idx: total };
  });

  /* ── conversation scrolling: follow new messages unless you're reading back ── */
  const scroller = useRef(null);
  const [atEnd, setAtEnd] = useState(true);
  const [unseen, setUnseen] = useState(false);
  const auto = useRef(0);                                 // until when a scroll is ours, not the learner's
  const onScroll = () => {
    const el = scroller.current;
    const end = el.scrollHeight - el.scrollTop - el.clientHeight < 90;
    if (Date.now() < auto.current && !end) return;
    setAtEnd(end); if (end) setUnseen(false);
  };
  const toEnd = (smooth = true) => {
    const el = scroller.current;
    if (!el) return;
    auto.current = Date.now() + 900;
    el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
    setAtEnd(true);
  };
  const contentKey = `${run.idx}:${run.results.length}:${rec ? 1 : 0}:${item ? 1 : 0}:${(draft.turns || []).length}:${draft.flipped ? 1 : 0}:${busy ? 1 : 0}:${err ? 1 : 0}`;
  const first = useRef(true);
  useLayoutEffect(() => {
    if (first.current) { first.current = false; toEnd(false); return; }
    if (atEnd) toEnd(); else setUnseen(true);
  }, [contentKey]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── summary ── */
  const summary = useMemo(() => {
    if (!done) return null;
    const R = run.results.filter((x) => x && !x.skipped);
    const by = (t) => R.filter((x) => x.type === t);
    const acc = (list) => (list.length ? list.filter((x) => x.correct).length / list.length : null);
    const avg = (list) => (list.length ? Math.round(list.reduce((s, x) => s + (x.score || 0), 0) / list.length) : null);
    const topics = {};
    R.forEach((x) => (x.concept_ids || []).slice(0, 1).forEach((cid) => {
      const k = String(cid).split('.').slice(0, 2).join('.');
      const v = x.score != null ? x.score : x.correct ? 100 : 0;
      topics[k] = topics[k] || { id: k, name: nameOf(k), sum: 0, n: 0 };
      topics[k].sum += v; topics[k].n += 1;
    }));
    const tl = Object.values(topics).map((t) => ({ ...t, avg: t.sum / t.n })).sort((a, b) => b.avg - a.avg);
    const revise = [...new Set(R.filter((x) => (x.correct === false || x.rating === 'again') && (x.concept_ids || [])[0]).map((x) => x.concept_ids[0]))];
    return {
      completed: R.length, skipped: total - R.length,
      mcq: { n: by('mcq').length, acc: acc(by('mcq')) }, rapid: { n: by('rapid').length, acc: acc(by('rapid')) },
      flash: { n: by('flashcards').length, recall: acc(by('flashcards')) },
      theory: { n: by('theory').length, avg: avg(by('theory')), gaps: [...new Set(by('theory').flatMap((x) => x.gaps || []))].slice(0, 4) },
      cases: { n: by('case').length, avg: avg(by('case')) },
      strong: tl.filter((t) => t.avg >= 70).slice(0, 3), weak: tl.filter((t) => t.avg < 60).reverse().slice(0, 3), topics: tl,
      revise: revise.slice(0, 6), minutes: Math.max(1, Math.round((Date.now() - run.startedAt) / 60000)),
    };
  }, [done, run, nameOf, total]);

  // a finished session joins "Your Practice Activity" on the Practice page
  useEffect(() => {
    if (!summary || run.recorded) return;
    saveSessionRecord({
      id: run.startedAt, ts: new Date().toISOString(), scopeLabel: cfg.scopeLabel, scopeIds: cfg.scopeIds, source: cfg.source,
      types: cfg.types, minutes: cfg.minutes, total, completed: summary.completed,
      mcqAccuracy: summary.mcq.n ? summary.mcq.acc : null, topics: summary.topics.map((t) => t.name).slice(0, 4),
    });
    setRun((r) => ({ ...r, recorded: true }));
  }, [summary, run.recorded, run.startedAt, cfg, total]);

  const completed = run.results.slice(0, total).filter((x) => x && !x.notReached).length;
  const kinds = [...new Set(steps.map((x) => x.type))];
  const firstName = displayName.split(' ')[0];

  /* ── the action dock: adapts to the live activity ── */
  const dock = () => {
    if (done) return null;
    if (err) {
      return (
        <div className="sc-dock-row">
          <span className="sc-dock-hint">This activity couldn’t load.</span>
          <button type="button" className="pu-btn pu-btn-quiet" onClick={() => setAttempt((n) => n + 1)}>Try again</button>
          <button type="button" className="pu-btn pu-btn-primary" onClick={skipActivity}>Skip to the next activity <IconArrowRight size={14} /></button>
        </div>
      );
    }
    if (!item) return <div className="sc-dock-row"><span className="sc-dock-hint">Preparing the next activity…</span></div>;
    const last = run.idx === total - 1;
    const nextBtn = (
      <button type="button" className="pu-btn pu-btn-primary" onClick={advance} autoFocus>
        {last ? 'See your summary' : 'Next activity'} <IconArrowRight size={14} />
      </button>
    );
    if (rec && !rec.skipped && step.type !== 'flashcards') {
      return <div className="sc-dock-row"><span className="sc-dock-hint">{last ? 'That was the last activity.' : `Up next: ${actOf(steps[run.idx + 1].type).one}`}</span>{nextBtn}</div>;
    }
    if (step.type === 'mcq') {
      return (
        <div className="sc-dock-row">
          <span className="sc-dock-hint">{draft.pick ? `Selected ${draft.pick}.` : 'Choose an option above.'}</span>
          <button type="button" className="pu-btn pu-btn-primary" disabled={!draft.pick} onClick={submitMcq}>Submit answer</button>
        </div>
      );
    }
    if (step.type === 'flashcards') {
      return draft.flipped ? (
        <div className="sc-dock-row">
          <span className="sc-dock-hint">Did you remember it?</span>
          <button type="button" className="pu-btn pu-btn-quiet" onClick={() => rateFlash('again')}>Review again</button>
          <button type="button" className="pu-btn pu-btn-primary" onClick={() => rateFlash('good')}>Got it <IconCheck size={14} /></button>
        </div>
      ) : (
        <div className="sc-dock-row">
          <span className="sc-dock-hint">Answer it in your head first.</span>
          <button type="button" className="pu-btn pu-btn-primary" onClick={() => setDraft({ ...draft, flipped: true })}>Reveal answer</button>
        </div>
      );
    }
    const isCase = step.type === 'case';
    const isRapid = step.type === 'rapid';
    const submit = isCase ? submitCase : isRapid ? submitRapid : submitTheory;
    const turns = draft.turns || [];
    return (
      <form className="sc-compose" onSubmit={(e) => { e.preventDefault(); submit(); }}>
        {isRapid ? (
          <input className="sc-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="A short answer — Enter to submit" aria-label="Your answer" autoFocus disabled={busy} />
        ) : (
          <textarea className={`sc-input${isCase ? ' tall' : ''}`} value={text} onChange={(e) => setText(e.target.value)} disabled={busy} autoFocus
            onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); submit(); } }}
            placeholder={isCase ? 'Structure your response: what you’d look at first, why, and what you’d do.' : 'Explain it in your own words — structure first, then detail.'}
            aria-label={isCase ? 'Your case-study response' : 'Your answer'} />
        )}
        <div className="sc-dock-row">
          <span className="sc-dock-hint">{actErr || (busy ? (isCase ? 'The coach is reading your response…' : 'Evaluating…') : isRapid ? '' : '⌘/Ctrl + Enter to submit')}</span>
          {!isCase && !isRapid && <button type="button" className="pu-btn pu-btn-quiet" disabled={busy} onClick={skipActivity}>Skip</button>}
          {isCase && turns.length > 0 && <button type="button" className="pu-btn pu-btn-quiet" disabled={busy} onClick={() => finishCase(turns)}>Finish the case</button>}
          <button type="submit" className="pu-btn pu-btn-primary" disabled={!text.trim() || busy}>{busy ? 'Submitting…' : isCase ? 'Submit response' : 'Submit answer'}</button>
        </div>
      </form>
    );
  };

  /* ── session flow (right panel) ── */
  const flow = (
    <>
      <section className="sc-panel" aria-labelledby="sc-flow-h">
        <div className="sc-panel-head"><h2 id="sc-flow-h">Session flow</h2><span>{completed} of {total}</span></div>
        <ol className="sc-flow">
          {steps.map((s, i) => {
            const r = run.results[i];
            const state = i < run.idx ? (r?.skipped ? 'skipped' : 'done') : i === run.idx ? 'current' : 'next';
            const it = r?.item || (i === run.idx ? item : peek(i));
            return (
              <li key={i} className={`sc-flow-${state}`} aria-current={state === 'current' ? 'step' : undefined}>
                <span className="sc-flow-mark" aria-hidden="true">{state === 'done' ? <IconCheck size={12} /> : state === 'skipped' ? <IconX size={11} /> : null}</span>
                <span className="sc-flow-n">{i + 1}</span>
                <span className="sc-flow-txt"><b>{actOf(s.type).one}</b><small>{topicOf(it) || (state === 'current' ? 'Loading…' : ' ')}</small></span>
                <span className="pu-sr">{state === 'done' ? ' — completed' : state === 'skipped' ? ' — skipped' : state === 'current' ? ' — current' : ' — upcoming'}</span>
              </li>
            );
          })}
        </ol>
      </section>
      <section className="sc-panel sc-details" aria-labelledby="sc-det-h">
        <h2 id="sc-det-h">Session details</h2>
        <dl>
          <div><dt>{cfg.source === 'topics' ? 'Topics' : 'Target role'}</dt><dd>{cfg.scopeLabel}</dd></div>
          <div><dt>Activity types</dt><dd>{kinds.map((k) => actOf(k).label).join(', ')}</dd></div>
          <div><dt>Session length</dt><dd>~{cfg.minutes} minutes</dd></div>
          <div><dt>Started at</dt><dd>{timeOf(run.startedAt)}</dd></div>
        </dl>
      </section>
    </>
  );

  const overlay = (
    <>
      {confirm && (
        <div className="kh-modal-veil" onMouseDown={(e) => { if (e.target === e.currentTarget) setConfirm(false); }}>
          <div className="kh-modal ses-confirm" role="alertdialog" aria-modal="true" aria-labelledby="ses-end-h">
            <h2 id="ses-end-h">End this session?</h2>
            <p>You’ll get a summary of what you’ve done so far. Answers already given are saved to your learning memory. To come back later instead, use Pause session.</p>
            <div className="kh-modal-foot">
              <button type="button" className="pu-btn pu-btn-quiet" autoFocus onClick={() => setConfirm(false)}>Keep going</button>
              <button type="button" className="pu-btn pu-btn-primary" onClick={() => { setConfirm(false); finishEarly(); }}>End &amp; see summary</button>
            </div>
          </div>
        </div>
      )}
      {flowOpen && (
        <div className="sc-drawer-veil" onMouseDown={(e) => { if (e.target === e.currentTarget) setFlowOpen(false); }}>
          <aside className="sc-drawer" aria-label="Session flow">
            <button type="button" className="sc-drawer-x" onClick={() => setFlowOpen(false)} aria-label="Close session flow"><IconX size={16} /></button>
            {flow}
          </aside>
        </div>
      )}
    </>
  );

  return (
    <IvShell hub={hub} className="sc-page" overlay={overlay} {...shell}>
      <header className="sc-top">
        <span className="sc-avatar lg" aria-hidden="true"><Tutor size={22} /></span>
        <div className="sc-top-title">
          <b>Practice Session</b>
          <span>{cfg.scopeLabel} · Mixed Practice</span>
        </div>
        <div className="sc-top-progress">
          <span>{completed} of {total} activities</span>
          <span className={`sc-bar${total > 30 ? ' solid' : ''}`} role="progressbar" aria-label="Session progress" aria-valuemin={0} aria-valuemax={total} aria-valuenow={completed}>
            {total > 30 ? <i style={{ width: `${(completed / total) * 100}%` }} /> : steps.map((_, i) => <i key={i} className={i < completed ? 'on' : i === run.idx && !done ? 'now' : ''} />)}
          </span>
        </div>
        <div className="sc-top-acts">
          <button type="button" className="pu-btn pu-btn-sm pu-btn-quiet sc-flow-btn" onClick={() => setFlowOpen(true)}>Session flow</button>
          {!done && <button type="button" className="pu-btn pu-btn-sm sc-pause" onClick={() => { saveRun(run); onExit(); }}><IconPause size={13} /> Pause session</button>}
          {!done && <button type="button" className="sc-end" onClick={() => setConfirm(true)} aria-label="End session"><IconX size={15} /></button>}
        </div>
      </header>

      <div className="sc-layout">
        <main className="sc-chat">
          <div className="sc-scroll" ref={scroller} onScroll={onScroll} role="log" aria-label="Practice conversation" aria-live="polite">
            <Bot at={run.startedAt}>
              <p>Hi{firstName ? ` ${firstName}` : ''}! Let’s start your practice session on <b>{cfg.scopeLabel}</b>. I’ll mix the question types as we go, so you recall, check and explain what you know.</p>
              <p className="sc-sub">This session includes {listJoin(kinds.map((k) => `${cfg.counts[k]} ${cfg.counts[k] === 1 ? actOf(k).one : actOf(k).label}`))}.</p>
            </Bot>

            {steps.slice(0, Math.min(run.idx + 1, total)).map((s, i) => {
              const r = run.results[i];
              const live = i === run.idx && !done;
              const it = live ? (r?.item || item) : r?.item;
              if (!it) {
                if (live) {
                  return err
                    ? <Bot key={i}><Head type={s.type} n={i + 1} total={total} /><p className="sc-err">{err}</p></Bot>
                    : <Bot key={i} className="typing"><span className="sc-typing" aria-label="Preparing the next activity"><i /><i /><i /></span></Bot>;
                }
                return <p key={i} className="sc-note">Activity {i + 1} · {actOf(s.type).one} — {r?.notReached ? 'not reached' : 'skipped'}</p>;
              }
              return (
                <Exchange key={i} step={s} idx={i} total={total} item={it} rec={r} live={live}
                  draft={live ? draft : {}} setDraft={setDraft} topic={topicOf(it)} initial={initial} />
              );
            })}

            {done && summary && (
              <>
                <Bot><p>That’s the end of your session — here’s how it went.</p></Bot>
                <section className="sc-summary" aria-labelledby="sc-sum-h">
                  <span className="pu-kicker">Session complete</span>
                  <h2 id="sc-sum-h">Your session summary</h2>
                  <p className="ses-sum-sub"><IconClock size={13} /> {summary.minutes} min · {summary.completed} of {total} activities completed{summary.skipped ? ` · ${summary.skipped} skipped or not reached` : ''}</p>
                  <div className="ses-sum-grid">
                    {summary.mcq.n > 0 && <div><small>MCQ accuracy</small><b>{pct(summary.mcq.acc)}</b><span>{summary.mcq.n} answered · objective</span></div>}
                    {summary.rapid.n > 0 && <div><small>Rapid fire</small><b>{pct(summary.rapid.acc)}</b><span>{summary.rapid.n} answered · graded</span></div>}
                    {summary.flash.n > 0 && <div><small>Flashcards recalled</small><b>{pct(summary.flash.recall)}</b><span>{summary.flash.n} reviewed · self-rated</span></div>}
                    {summary.theory.n > 0 && <div><small>Theory</small><b>{summary.theory.avg}<em>/100</em></b><span>{summary.theory.n} answers · AI-evaluated</span></div>}
                    {summary.cases.n > 0 && <div><small>Case study</small><b>{Math.round(summary.cases.avg / 10)}<em>/10</em></b><span>{summary.cases.n} case{summary.cases.n > 1 ? 's' : ''} · AI-evaluated</span></div>}
                  </div>
                  <div className="ses-sum-cols">
                    <section><h3>Strengths</h3>{summary.strong.length ? <ul>{summary.strong.map((t) => <li key={t.id}>{t.name}</li>)}</ul> : <p className="ses-dim">Nothing above 70 this time.</p>}</section>
                    <section><h3>Needs more practice</h3>{summary.weak.length ? <ul>{summary.weak.map((t) => <li key={t.id}>{t.name}</li>)}</ul> : <p className="ses-dim">No weak spots this session.</p>}</section>
                    <section><h3>Concepts to revise</h3>{summary.revise.length ? <ul>{summary.revise.map((c) => <li key={c}><button type="button" className="pu-link" onClick={() => onOpenHubNode?.(c)}>{nameOf(c, 9)}</button></li>)}</ul> : <p className="ses-dim">Nothing flagged.</p>}</section>
                  </div>
                  {summary.theory.gaps.length > 0 && <p className="ses-sum-gaps"><b>From your theory answers:</b> {summary.theory.gaps.join('; ')}.</p>}
                  <p className="ses-dim">Topics covered: {summary.topics.map((t) => t.name).join(', ') || '—'}. Your answers are saved to your learning memory.</p>
                  <div className="ses-next">
                    <button type="button" className="ses-next-card" disabled={!summary.weak[0]} onClick={() => { clearRun(); onNewSession({ topics: summary.weak.map((t) => ({ id: t.id, name: t.name })), types: cfg.types, minutes: cfg.minutes }); }}>
                      <b>Practice weak areas</b><span>{summary.weak[0] ? `A new session on ${summary.weak.map((t) => t.name).join(', ')}.` : 'No weak areas flagged this session.'}</span><IconArrowRight size={14} />
                    </button>
                    <button type="button" className="ses-next-card" onClick={() => onOpenHubNode?.(summary.revise[0] || cfg.scopeIds[0])}>
                      <b>Review concepts</b><span>Revisit the ideas behind this session in the Knowledge Hub.</span><IconBook size={14} />
                    </button>
                    <button type="button" className="ses-next-card primary" onClick={() => { clearRun(); onNewSession(cfg.source === 'topics' ? { topics: cfg.scopeIds.map((id) => ({ id, name: byId[id]?.name || id })), types: cfg.types, minutes: cfg.minutes } : { types: cfg.types, minutes: cfg.minutes }); }}>
                      <b>Start another session</b><span>Same setup — or pick a new mix.</span><IconArrowRight size={14} />
                    </button>
                  </div>
                  <button type="button" className="pu-link ses-back" onClick={() => { clearRun(); onExit(); }}>Back to Practice</button>
                </section>
              </>
            )}
          </div>
          {unseen && !atEnd && (
            <button type="button" className="sc-jump" onClick={() => { toEnd(); setUnseen(false); }}><IconArrowDown size={13} /> Jump to latest</button>
          )}
          {!done && <div className="sc-dock">{dock()}</div>}
        </main>
        <aside className="sc-side" aria-label="Session flow and details">{flow}</aside>
      </div>
    </IvShell>
  );
}
